import { ReactNode } from 'react';
import { getUserDisplayName } from 'shared-helpers/common';
import { getMessageWithPlaceholders } from 'shared-locales';
import { useReduxState } from 'hooks';
import { getCustomizedNavTitle } from 'lib/helpers';
import {
  getMyTeamFollowHref,
  getMyTeamReviewHref
} from 'lib/supervisors';
import { Alert, FlexContainer, User } from 'components/common';
import { ErrorPage } from 'components/error';
import { Page, PageContent, PageHeader, PageNav } from 'components/page';

import type { IconProp } from 'components/common/icons/Icon';
import type { NavLink } from 'types';

interface Props {
  activeLink: string;
  children: ReactNode;
  withSidebar?: boolean;
}

const MyTeamPage = ({ activeLink, children, withSidebar = false }: Props) => {
  const {
    language,
    messages,
    pageState: { supervisor },
    serverConfig,
    theme,
    user
  } = useReduxState();

  if (!user?.roles?.includes('supervisor') || !supervisor) {
    return <ErrorPage statusCode={404} />;
  }

  const hasPendingAccessRequests = supervisor?.pendingAccessRequests > 0;

  const links: NavLink[] = [
    {
      href: '/my-team',
      icon: 'home',
      title: messages.commonSummary
    },
    {
      href: '/my-team/users',
      icon: 'user',
      title: messages.adminSupervisorsSupervisees
    },
    ...(serverConfig.platformCompetenciesOnly
      ? []
      : [
          {
            href: getMyTeamFollowHref({
              hasPendingAccessRequests,
              serverConfig
            }),
            icon: 'chart-line' as const,
            title: messages.commonAnalytics,
            unreadItems: supervisor?.pendingAccessRequests
          },
          {
            href: '/my-team/completions',
            icon: 'history' as const,
            title: messages.profileCompletionHistory
          },
          {
            href: '/my-team/completion-matrix',
            icon: 'table' as const,
            title: messages.completionMatrix
          },
          {
            href: getMyTeamReviewHref({ serverConfig }),
            icon: ['far', 'clock'] as IconProp,
            title: messages.myTeamPageReview,
            unreadItems: supervisor.unreviewedItems
          }
        ]),
    {
      href: '/my-team/settings',
      icon: 'cog',
      title: messages.commonSettings
    }
  ];

  return (
    <Page activePathname="/my-team">
      <PageHeader
        icon="sitemap"
        title={getCustomizedNavTitle({
          defaultTitle: messages.myTeamPage,
          navLinksCustomTexts: serverConfig.navLinksCustomTexts,
          language,
          section: 'myteam'
        })}
      />

      <PageNav activeLink={`/my-team${activeLink}`} links={links}>
        <FlexContainer style={{ gap: 10 }}>
          <User showNameOrUsername={false} user={user} />

          <div>
            <div
              style={{
                color: theme.textColor,
                fontSize: '1.5rem',
                fontWeight: 700
              }}
            >
              <span>
                {getUserDisplayName({ messages, serverConfig, user })}
              </span>
              <span style={{ margin: '0 7px' }}>{'•'}</span>
              <span>{messages.myTeamTeamPage}</span>
            </div>
            <div
              style={{
                color: theme.textColorLighter,
                fontSize: '1.4rem',
                fontWeight: 600
              }}
            >
              {getMessageWithPlaceholders({
                message: messages.myTeamNumberOfSupervisedUsers,
                placeholders: { numberOfUsers: supervisor.numberOfSupervisees }
              })}
            </div>
          </div>
        </FlexContainer>
      </PageNav>

      {!supervisor.numberOfSupervisees ? (
        <div className="container" style={{ marginTop: 20 }}>
          <Alert
            size="large"
            type="warning-light"
            text={messages.myTeamPageNoSupervisedUsers}
          />
        </div>
      ) : null}

      <PageContent withSidebar={withSidebar}>{children}</PageContent>
    </Page>
  );
};

export default MyTeamPage;
